import { useMediaQuery, useTheme } from '@mui/material';
import { useEffect, useRef } from 'react';
import { useSessionStorage, useTimeout } from 'react-use';

import { SIDEBAR_ANIMATION_TIMING_MS } from '@/components/architecture/Sidebar/sidebarConstants';

export type SidebarExpansionState = 'collapsed' | 'expanded';

export function useSidebarExpansionState(storageKey = 'sidebarExpansionState') {
  const theme = useTheme();
  const isCompactViewport = useMediaQuery(theme.breakpoints.down('lg'));
  const wasCompactRef = useRef<boolean | undefined>(undefined);

  const [expansionState, setExpansionStateInternal] =
    useSessionStorage<SidebarExpansionState>(storageKey, 'expanded');

  const [isAnimationComplete, _, startAnimationTimer] = useTimeout(
    SIDEBAR_ANIMATION_TIMING_MS
  );

  // Auto-collapse/expand when the viewport crosses the lg breakpoint.
  // wasCompactRef starts as undefined so the first render also applies
  // the correct state for the current viewport size.
  useEffect(() => {
    if (isCompactViewport && !wasCompactRef.current) {
      setExpansionStateInternal('collapsed');
      startAnimationTimer();
    } else if (!isCompactViewport && wasCompactRef.current) {
      setExpansionStateInternal('expanded');
      startAnimationTimer();
    }
    wasCompactRef.current = isCompactViewport;
  }, [isCompactViewport, setExpansionStateInternal, startAnimationTimer]);

  const isExpanded = expansionState !== 'collapsed';

  const setExpansionState = (next: SidebarExpansionState) => {
    setExpansionStateInternal(next);
    startAnimationTimer();
  };

  const toggle = () => {
    setExpansionState(isExpanded ? 'collapsed' : 'expanded');
  };

  return {
    expansionState,
    isExpanded,
    setExpansionState,
    toggle,
    isTooltipReady: () => isAnimationComplete() ?? false,
  };
}
